import { useState, useEffect, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import { getTestPublic, submitTestPublic } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Briefcase, Clock, Loader2, AlertTriangle, Camera, Eye } from "lucide-react";
import { toast } from "sonner";
import { useProctorController } from "@/hooks/useProctorController";
import { logger } from "@/lib/logger";
import BrandedHeader from "@/components/BrandedHeader";
import PreScreeningSection, { validatePreScreening } from "@/components/take-test/PreScreeningSection";
import QuestionNav from "@/components/take-test/QuestionNav";
import QuestionPane from "@/components/take-test/QuestionPane";
import TestResult from "@/components/take-test/TestResult";

function formatTime(seconds) {
  if (seconds === null) return "--:--";
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
}

function extractErrorString(detail, fallback) {
  if (!detail) return fallback;
  if (typeof detail === "string") return detail;
  return fallback;
}

export default function PublicTestPage() {
  const { testId } = useParams();
  const [testData, setTestData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [showPreScreening, setShowPreScreening] = useState(true);
  const [preScreeningAnswers, setPreScreeningAnswers] = useState({});
  const [answers, setAnswers] = useState({});
  const [currentQ, setCurrentQ] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [timeLeft, setTimeLeft] = useState(null);
  const timerRef = useRef(null);

  const proctor = useProctorController({
    enabled: !result && !showPreScreening,
    onAnomalyDetected: (anomaly) => logger.debug("Anomaly detected:", anomaly),
  });

  const loadTest = useCallback(async () => {
    try {
      const res = await getTestPublic(testId);
      setTestData(res.data);
      if (res.data.pre_screening_questions?.length > 0) {
        setShowPreScreening(true);
      } else {
        setShowPreScreening(false);
        setTimeLeft((res.data.time_limit_minutes || 30) * 60);
      }
    } catch (e) {
      console.error("Load test error:", e);
      setError(extractErrorString(e.response?.data?.detail, "Invalid or expired test link"));
    } finally {
      setLoading(false);
    }
  }, [testId]);

  const handleSubmit = useCallback(async () => {
    if (Object.keys(answers).length === 0) {
      toast.error("Please answer at least one question before submitting");
      return;
    }
    setSubmitting(true);
    try {
      const cleanAnswers = Object.fromEntries(
        Object.entries(answers).map(([k, v]) => [String(k), String(v)])
      );
      const cleanPreScreening = Object.fromEntries(
        Object.entries(preScreeningAnswers || {}).map(([k, v]) => [String(k), String(v || "")])
      );
      const cleanAnomalyLogs = (proctor.anomalyLogs || []).map((log) => ({
        event_type: String(log.event_type || "UNKNOWN"),
        details: String(log.details || ""),
        severity: String(log.severity || "low"),
        timestamp: String(log.timestamp || new Date().toISOString()),
      }));
      const res = await submitTestPublic(testId, {
        answers: cleanAnswers,
        pre_screening_answers: cleanPreScreening,
        anomaly_logs: cleanAnomalyLogs,
      });
      setResult(res.data);
      clearTimeout(timerRef.current);
      toast.success("Test submitted successfully!");
    } catch (e) {
      console.error("Submit error:", e);
      toast.error(extractErrorString(e.response?.data?.detail, "Failed to submit test"), { duration: 6000 });
    } finally {
      setSubmitting(false);
    }
  }, [answers, preScreeningAnswers, proctor.anomalyLogs, testId]);

  useEffect(() => {
    loadTest();
  }, [loadTest]);

  useEffect(() => {
    if (timeLeft !== null && timeLeft > 0 && !result && !showPreScreening) {
      timerRef.current = setTimeout(() => setTimeLeft((t) => t - 1), 1000);
      return () => clearTimeout(timerRef.current);
    }
    if (timeLeft === 0 && !result) {
      handleSubmit();
    }
  }, [timeLeft, result, showPreScreening, handleSubmit]);

  const handlePreScreeningSubmit = () => {
    const err = validatePreScreening(testData?.pre_screening_questions || [], preScreeningAnswers);
    if (err) {
      toast.error(err);
      return;
    }
    setShowPreScreening(false);
    setTimeLeft((testData.time_limit_minutes || 30) * 60);
    toast.success("Pre-screening complete. Starting assessment...");
  };

  const handleSelect = (qIdx, option) => setAnswers({ ...answers, [String(qIdx)]: option });

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-primary animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <Card className="max-w-md w-full border-border bg-card">
          <CardContent className="p-8 text-center">
            <AlertTriangle className="w-12 h-12 text-[hsl(var(--warning))] mx-auto mb-4" />
            <h2 className="text-xl font-display font-semibold mb-2">Access Error</h2>
            <p className="text-muted-foreground">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (result) return <TestResult result={result} />;

  if (showPreScreening) {
    return (
      <PreScreeningSection
        questions={testData?.pre_screening_questions || []}
        answers={preScreeningAnswers}
        setAnswers={setPreScreeningAnswers}
        onSubmit={handlePreScreeningSubmit}
        headerTitle={testData?.test_title}
      />
    );
  }

  const questions = testData?.questions || [];
  const totalQ = questions.length;
  const answered = Object.keys(answers).length;
  const isTimeLow = timeLeft !== null && timeLeft < 120;
  const camOff = !proctor.cameraActive;

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <BrandedHeader className="flex-shrink-0">
        <div className="flex items-center gap-3 flex-1">
          <Briefcase className="w-5 h-5 text-primary" />
          <div>
            <span className="font-display font-semibold text-sm">{testData?.test_title}</span>
          </div>
          <div className="flex items-center gap-3 ml-auto">
            <div className="hidden sm:flex items-center gap-2">
              <Badge variant="secondary" className={`text-xs ${proctor.cameraActive ? "text-[hsl(var(--success))]" : "text-[hsl(var(--destructive))]"}`}>
                <Camera className="w-3 h-3 mr-1" /> {proctor.cameraActive ? "ON" : "OFF"}
              </Badge>
              <Badge variant="secondary" className={`text-xs ${proctor.faceDetected ? "text-[hsl(var(--success))]" : "text-[hsl(var(--warning))]"}`}>
                <Eye className="w-3 h-3 mr-1" /> {proctor.faceDetected ? "OK" : "NO FACE"}
              </Badge>
            </div>
            <Badge variant="secondary" className={`font-mono text-xs ${isTimeLow ? "text-[hsl(var(--destructive))] animate-pulse" : ""}`}>
              <Clock className="w-3 h-3 mr-1" /> {formatTime(timeLeft)}
            </Badge>
            <span className="text-xs text-muted-foreground">{answered}/{totalQ}</span>
            <Button onClick={handleSubmit} disabled={submitting || answered === 0 || camOff} size="sm" className="btn-press" data-testid="public-submit-test-button">
              {submitting ? <Loader2 className="w-3 h-3 animate-spin" /> : "Submit"}
            </Button>
          </div>
        </div>
      </BrandedHeader>

      {(proctor.tabSwitchCount >= 2 || camOff || !proctor.faceDetected) && (
        <div className="bg-[hsl(var(--destructive))]/10 border-b border-[hsl(var(--destructive))]/25 px-4 py-2">
          <div className="max-w-3xl mx-auto flex items-center gap-2 text-sm">
            <AlertTriangle className="w-4 h-4 text-[hsl(var(--destructive))] flex-shrink-0" />
            <p className="text-[hsl(var(--destructive))]">
              {camOff
                ? "Camera is required for proctoring - You cannot proceed until camera is enabled"
                : !proctor.faceDetected
                ? "Please ensure your face is visible"
                : "Multiple integrity warnings detected"}
            </p>
          </div>
        </div>
      )}

      {camOff && (
        <div className="bg-[hsl(var(--destructive))]/20 border-b border-[hsl(var(--destructive))]/30 px-4 py-4">
          <div className="max-w-3xl mx-auto bg-card border-2 border-[hsl(var(--destructive))] rounded-lg p-6 text-center">
            <Camera className="w-12 h-12 text-[hsl(var(--destructive))] mx-auto mb-3" />
            <h3 className="text-lg font-semibold mb-2">Camera Required</h3>
            <p className="text-sm text-muted-foreground mb-4">
              You must enable your camera to continue with this assessment. Please grant camera permissions and ensure your camera is working.
            </p>
            <Badge variant="destructive" className="text-xs">Test navigation is blocked</Badge>
          </div>
        </div>
      )}

      <div className="flex-1 flex overflow-hidden">
        <div className="flex-1 px-4 sm:px-6 py-6 overflow-auto">
          <div className="max-w-3xl mx-auto">
            <div className="mb-6">
              <div className="flex justify-between text-xs text-muted-foreground mb-2">
                <span>Question {currentQ + 1} of {totalQ}</span>
                <span>{Math.round((answered / totalQ) * 100)}% complete</span>
              </div>
              <Progress value={(answered / totalQ) * 100} className="h-1.5" />
            </div>

            <QuestionNav questions={questions} currentQ={currentQ} answers={answers} onSelect={setCurrentQ} disabled={camOff} />

            <QuestionPane
              question={questions[currentQ]}
              index={currentQ}
              totalQ={totalQ}
              answers={answers}
              onSelect={handleSelect}
              onPrev={() => setCurrentQ(Math.max(0, currentQ - 1))}
              onNext={() => setCurrentQ(currentQ + 1)}
              onSubmit={handleSubmit}
              submitting={submitting}
              disabled={camOff}
            />
          </div>
        </div>

        <div className="hidden lg:block w-[280px] border-l border-border p-4 space-y-3">
          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Proctoring</h3>
          <div className="aspect-video rounded-lg bg-black/40 overflow-hidden relative">
            {proctor.cameraActive ? (
              <video ref={proctor.videoRef} autoPlay muted playsInline className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Camera className="w-6 h-6 text-muted-foreground" />
              </div>
            )}
            <div className="absolute top-2 right-2">
              <div className={`w-2.5 h-2.5 rounded-full ${proctor.cameraActive && proctor.faceDetected ? "bg-[hsl(var(--success))]" : "bg-[hsl(var(--destructive))]"}`} />
            </div>
          </div>
          <div className="space-y-2 text-xs">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Tab Switches</span>
              <Badge variant="secondary" className={`text-xs ${proctor.tabSwitchCount > 2 ? "text-[hsl(var(--destructive))]" : ""}`}>
                {proctor.tabSwitchCount}
              </Badge>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
